import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";

import { Decision, EroundMappingView, ProvisioningDetail, StationDataView } from "../../../../types/eroundProvisioning";
import { actorLabel } from "../../../../utils/actors";
import {
  approveProvisioning,
  getProvisioning,
  rejectProvisioning,
  retryProvisioning,
} from "../../../../services/eroundProvisioning";
import {
  canApprove,
  canRetry,
  manualActionGuidance,
  missingLabel,
  missingWaitingOn,
  statusLabel,
} from "../../../../utils/eroundProvisioning";
import { markedBy, offersLadeverbund } from "./LadeverbundToggle";
import { ProvisioningAction, provisioningErrorMessage } from "./provisioningErrors";
import ProvisioningStatusTag from "./ProvisioningStatusTag";

import { WALLBOX_MODELS_PATH } from "../../../../utils/stationSetup";

export { WALLBOX_MODELS_PATH };

export const MAX_REASON = 500;

interface ProvisioningDialogProps {
  record: ProvisioningDetail;
  visible: boolean;
  onHide: () => void;
  // Re-reads the record; the refreshed one comes back in through `record`.
  onChanged: () => Promise<unknown> | void;
  // False on the prospect page, where approval goes through "Set up in eRound + OCPP" (CWA-231).
  approvable?: boolean;
}

type Notice = { severity: "success" | "warn" | "error"; text: string };

const formatDate = (iso?: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid m-0 py-1">
    <div className="col-5 p-0 text-600">{label}</div>
    <div className="col-7 p-0" aria-label={label}>
      {children}
    </div>
  </div>
);

const decisionText = (decision: Decision): string =>
  `${actorLabel(decision.actorEmail ?? decision.actorId) || decision.actorId}, ${formatDate(decision.at)}`;

const WAITING_ON_TAG: Record<string, string> = { SUPPORT: "Support", CUSTOMER: "Customer" };

const NO_STATION: StationDataView = {
  manufacturer: null,
  model: null,
  chargingPoints: null,
  locationType: null,
  street: null,
  postcode: null,
  city: null,
  country: null,
};

const gapText = (piece: string, station: StationDataView, mapping: EroundMappingView | null): string =>
  piece === "CHARGING_POINTS_MISMATCH" && mapping && station.chargingPoints !== null
    ? `${missingLabel(piece)}: the station has ${station.chargingPoints}, the eRound model ${mapping.chargingPoints}`
    : missingLabel(piece);

const mappingNote = (mapping: EroundMappingView): string =>
  mapping.source === "catalogue"
    ? `From the Wallbox Catalogue, ${formatDate(mapping.setAt)}`
    : `Set by hand by ${actorLabel(mapping.setBy) || mapping.setBy}, ${formatDate(mapping.setAt)}`;

const addressText = (station: StationDataView): string =>
  [station.street, [station.postcode, station.city].filter(Boolean).join(" "), station.country]
    .filter(Boolean)
    .join(", ") || "—";

const exceedsModel = (station: StationDataView, mapping: EroundMappingView | null): boolean =>
  mapping !== null && station.chargingPoints !== null && station.chargingPoints > mapping.chargingPoints;

/** The station as onboarding describes it next to the eRound model it maps to (SOPS-165). */
const ModelComparison: React.FC<{
  station: StationDataView;
  mapping: EroundMappingView | null;
  // A marked Ladeverbund's extra charging points are intended (SOPS-174).
  chargingNetwork: boolean;
}> = ({ station, mapping, chargingNetwork }) => {
  const mismatch =
    mapping !== null &&
    station.chargingPoints !== null &&
    station.chargingPoints !== mapping.chargingPoints &&
    !(chargingNetwork && exceedsModel(station, mapping));
  return (
    <>
      <table aria-label="Station and eRound model" className="w-full mb-2" style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr className="text-600 text-left">
            <th />
            <th className="font-normal">Station</th>
            <th className="font-normal">eRound</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row" className="text-600 font-normal text-left py-1">Manufacturer</th>
            <td>{station.manufacturer ?? "—"}</td>
            <td>{mapping?.manufacturer ?? "—"}</td>
          </tr>
          <tr>
            <th scope="row" className="text-600 font-normal text-left py-1">Model</th>
            <td>{station.model ?? "—"}</td>
            <td>{mapping?.model ?? "—"}</td>
          </tr>
          <tr>
            <th scope="row" className="text-600 font-normal text-left py-1">Charging points</th>
            <td>{station.chargingPoints ?? "—"}</td>
            {/* PrimeFlex colour classes are not loaded in this app; the theme's p-error is. */}
            <td className={mismatch ? "p-error font-bold" : undefined}>{mapping?.chargingPoints ?? "—"}</td>
          </tr>
        </tbody>
      </table>
      {mapping?.standIn && (
        <div className="mb-2" aria-label="Stand-in">
          <Tag value="Stand-in" severity="warning" className="mr-2" />
          eRound doesn't list this wallbox; it runs as {mapping.manufacturer} – {mapping.model}. {mapping.standIn.reason}{" "}
          <span className="text-600">(eRound support: {mapping.standIn.supportReference})</span>
        </div>
      )}
      <div className="text-600">
        {mapping ? (
          mappingNote(mapping)
        ) : (
          <>
            Not mapped to an eRound model yet – <Link to={WALLBOX_MODELS_PATH}>record eRound's names</Link>
          </>
        )}
      </div>
    </>
  );
};

/**
 * One station's eRound provisioning record (SOPS-148): what is missing, what
 * will be sent, the outcome and who decided what. The OCPP key is shown only
 * as present or missing; the record holds nothing more.
 */
const ProvisioningDialog: React.FC<ProvisioningDialogProps> = ({ record, visible, onHide, onChanged, approvable = true }) => {
  const { request, lastResult } = record;
  const station = record.station ?? NO_STATION;
  const mapping = record.eroundMapping ?? null;
  const [pending, setPending] = useState<ProvisioningAction | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const trimmedReason = reason.trim();

  // station-ops answers a conflict with a bare 409, whether the record moved
  // on or its data changed since it was opened. Re-reading it tells which
  // (research R3); either way the dialog then shows the current record.
  const explainConflict = async (statusWhenOpened: string): Promise<Notice> => {
    const current = await getProvisioning(record.stationId).catch(() => null);
    await onChanged();
    if (current && current.status === statusWhenOpened) {
      return {
        severity: "warn",
        text: "The station's data changed since you opened it. Check the request below again.",
      };
    }
    return {
      severity: "warn",
      text: `Already handled: the station is now "${statusLabel(current?.status ?? "NOT_TRACKED")}".`,
    };
  };

  const run = async (action: ProvisioningAction, call: () => Promise<unknown>, done: string) => {
    const statusWhenOpened = record.status;
    setPending(action);
    setNotice(null);
    try {
      await call();
      setRejecting(false);
      setReason("");
      setNotice({ severity: "success", text: done });
      await onChanged();
    } catch (err: any) {
      setNotice(
        err?.response?.status === 409
          ? await explainConflict(statusWhenOpened)
          : { severity: "error", text: provisioningErrorMessage(err, action) }
      );
    } finally {
      setPending(null);
    }
  };

  const approve = () =>
    run("approve", () => approveProvisioning(record.stationId, record.fingerprint ?? ""), "Approved. The station is on its way to eRound.");
  const reject = () =>
    run("reject", () => rejectProvisioning(record.stationId, trimmedReason), "Rejected. Set the station up in eRound by hand.");
  const retry = () => run("retry", () => retryProvisioning(record.stationId), "Sent to eRound again.");
  const marked = record.chargingNetwork ?? null;

  const footer = (
    <div className="flex flex-wrap justify-content-end gap-2">
      {canApprove(record) && !rejecting && (
        <>
          <Button label="Reject…" severity="danger" outlined disabled={pending !== null} onClick={() => setRejecting(true)} />
          {approvable && (
            <Button label="Approve" icon="pi pi-check" loading={pending === "approve"} disabled={pending !== null} onClick={approve} />
          )}
        </>
      )}
      {canRetry(record) && (
        <Button label="Retry" icon="pi pi-refresh" loading={pending === "retry"} disabled={pending !== null} onClick={retry} />
      )}
      <Button label="Close" severity="secondary" outlined disabled={pending !== null} onClick={onHide} />
    </div>
  );

  return (
    <Dialog
      header={`eRound provisioning – ${record.stationId}`}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "44rem" }}
      breakpoints={{ "960px": "90vw" }}
    >
      <div className="flex align-items-center gap-2 mb-3">
        <ProvisioningStatusTag status={record.status} waitingOn={record.waitingOn} />
        {record.attempt > 1 && <span className="text-600">Attempt {record.attempt}</span>}
      </div>

      {notice && <Message severity={notice.severity} className="w-full mb-3" text={notice.text} />}

      {!approvable && canApprove(record) && (
        <Message
          severity="info"
          className="w-full mb-3"
          text={'Approve it with "Set up in eRound + OCPP" in the Next step, which promotes the customer first.'}
        />
      )}

      {rejecting && (
        <section className="mb-3">
          <label htmlFor="provisioning-reject-reason" className="block mb-2">
            Why is this station set up by hand?
          </label>
          <InputTextarea
            id="provisioning-reject-reason"
            className="w-full"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex justify-content-between align-items-center mt-2">
            <span className={trimmedReason.length > MAX_REASON ? "text-red-500" : "text-500"}>
              {trimmedReason.length} / {MAX_REASON}
            </span>
            <div className="flex gap-2">
              <Button label="Cancel" severity="secondary" text disabled={pending !== null} onClick={() => setRejecting(false)} />
              <Button
                label="Reject"
                severity="danger"
                loading={pending === "reject"}
                disabled={pending !== null || !trimmedReason || trimmedReason.length > MAX_REASON}
                onClick={reject}
              />
            </div>
          </div>
        </section>
      )}

      {record.status === "NEEDS_MANUAL_ACTION" && (
        <Message severity="error" className="w-full mb-3" text={manualActionGuidance(record.manualActionReason)} />
      )}

      {record.alreadyInEround && (
        <Message
          severity="info"
          className="w-full mb-3"
          text="A location or station with this name already exists in eRound. It will be reused, not duplicated."
        />
      )}

      {record.missing.length > 0 && (
        <section className="mb-3">
          <h4 className="mt-0 mb-2">What's missing</h4>
          <ul className="m-0 pl-4">
            {record.missing.map((piece) => (
              <li key={piece} className="mb-1">
                <span className="mr-2">
                  {gapText(piece, station, mapping)}
                  {piece === "MODEL_NOT_MAPPED" && (
                    <>
                      {" – "}
                      <Link to={WALLBOX_MODELS_PATH}>record eRound's names</Link>
                    </>
                  )}
                </span>
                {missingWaitingOn(piece) && (
                  <Tag
                    value={WAITING_ON_TAG[missingWaitingOn(piece)!]}
                    severity={missingWaitingOn(piece) === "SUPPORT" ? "warning" : "info"}
                  />
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {(station.model || mapping) && (
        <section className="mb-3">
          <h4 className="mt-0 mb-2">Station and eRound model</h4>
          <ModelComparison station={station} mapping={mapping} chargingNetwork={marked !== null} />
          {/* Set in the stations table, where support works; the review only shows it (SOPS-174). */}
          {offersLadeverbund({ ...record, station, eroundMapping: mapping }) && (
            <div className="mt-2" aria-label="Ladeverbund">
              {marked ? (
                <>
                  <i className="pi pi-check-square mr-2" />
                  Ladeverbund (master/slave): one station with all {station.chargingPoints} charging points on this
                  model. Marked by {markedBy(marked)}.
                </>
              ) : (
                <span className="text-600">
                  A Ladeverbund (master/slave)? Tick Ladeverbund in the stations table to create one station with all{" "}
                  {station.chargingPoints} charging points.
                </span>
              )}
            </div>
          )}
          {!request && (
            <div className="mt-2">
              <Row label="Location type">{station.locationType ?? "—"}</Row>
              <Row label="Address">{addressText(station)}</Row>
            </div>
          )}
        </section>
      )}

      {request && (
        <section className="mb-3">
          <h4 className="mt-0 mb-2">What will be created in eRound</h4>
          <Row label="Location">{request.location.locationName}</Row>
          <Row label="Location type">{request.location.locationType}</Row>
          <Row label="Address">{request.location.address}</Row>
          <Row label="Street">{request.location.street}</Row>
          <Row label="Postcode">{request.location.postcode}</Row>
          <Row label="Country">{request.location.country}</Row>
          <Row label="Coordinates">
            {request.location.latitude !== null && request.location.longitude !== null
              ? `${request.location.latitude}, ${request.location.longitude}`
              : "—"}
          </Row>
          <Row label="Visibility">{request.location.publiclyVisible ? "Public" : "Private"}</Row>
          <Row label="Station">{request.station.stationName}</Row>
          <Row label="Station location">{request.station.location}</Row>
          <Row label="Wallbox">{`${request.station.manufacturer} – ${request.station.model}`}</Row>
          <Row label="Charging points">{request.station.numberOfChargingPoints}</Row>
          <Row label="EVSE ids">
            {request.station.evseDetails.map((evse) => evse.evseId).join(", ") || "—"}
          </Row>
          <Row label="OCPP key">{record.ocppSecretPresent ? "present" : "missing"}</Row>
        </section>
      )}

      {lastResult && (
        <section className="mb-3">
          <h4 className="mt-0 mb-2">Latest outcome</h4>
          <Row label="Result">{statusLabel(lastResult.status)}</Row>
          {lastResult.reason && <Row label="Reason">{lastResult.reason}</Row>}
          {lastResult.eroundLocationId && <Row label="eRound location">{lastResult.eroundLocationId}</Row>}
          {lastResult.eroundStationId && <Row label="eRound station">{lastResult.eroundStationId}</Row>}
          <Row label="Finished">{formatDate(lastResult.finishedAt)}</Row>
        </section>
      )}

      <section>
        <h4 className="mt-0 mb-2">History</h4>
        <Row label="Contract signed">{formatDate(record.contractSignedAt)}</Row>
        {record.approval && <Row label="Approved by">{decisionText(record.approval)}</Row>}
        {record.rejection && (
          <Row label="Rejected by">
            {decisionText(record.rejection)}
            {record.rejection.reason && <div className="text-600">{record.rejection.reason}</div>}
          </Row>
        )}
        <Row label="Last change">{formatDate(record.updatedAt)}</Row>
      </section>
    </Dialog>
  );
};

export default ProvisioningDialog;
